import React from "react";
import get from "lodash/get";
import moment from "moment-timezone";
import {Row, Col, Layout, Alert, Icon, Descriptions, Button} from "antd";

import classes from "./PatientDashboard.css";
import { getUserToken } from "../../reducers/user/selectors";
import { getTheme } from "../../reducers/theme/selectors";
import { getPatientProfile } from "../../api/requests";
import { login, loginTwoFactor } from "../../reducers/user/actions";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import isPatient from "../../utils/isPatient";
import FileLink from "../../components/FileLink/FileLink";
import map from "lodash/map";
import {Link} from "react-router-dom";
import {withParams} from "../../utils/url";

const { Content } = Layout;


const emailRegex = new RegExp(
  /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi
);

class EventMessage extends React.Component {
  renderMessagePart(messagePart) {
    if (emailRegex.test(messagePart)) {
      return (
        <a href={`mailto:${messagePart}`} target="_blank">
          {messagePart}
        </a>
      );
    }

    return messagePart;
  }

  render() {
    return (
      <span>
        {this.props.message.split(emailRegex).map((messagePart, index) => (
          <React.Fragment key={index}>
            {this.renderMessagePart(messagePart)}
          </React.Fragment>
        ))}
      </span>
    );
  }
}


class PatientDashboard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      patientProfile: null,
      isLoading: false,
      errorMessage: "",
      previewEvents: {},
    };
  }

  componentDidMount() {
    this.getPatientProfile();
  }

  getPatientProfile = async () => {
    this.setState({
      isLoading: true,
    });
    try {
      const { data } = await getPatientProfile();
      this.setState({
        patientProfile: data,
      });
    } catch (e) {
      console.log(e);
      this.setState({
        errorMessage: get(e, "response.message", this.props.i18n.t("Error")),
      });
    }

    this.setState({
      isLoading: false,
    });
  };

  togglePreviewEvents = (eventName) => () => {
    this.setState((state) => {
      const eventStatus = state.previewEvents[eventName] ? false : true;
      return {
        previewEvents: { ...state.previewEvents, [eventName]: eventStatus },
      };
    });
  };

  render() {
    const { patientProfile, isLoading, errorMessage, previewEvents } = this.state;

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <Content className={classes.content}>
            <Row>
              <Col>
                <h2 className="headline">{this.props.i18n.t("Patient info.")}</h2>
                {isLoading && <Icon type="loading" />}

                {patientProfile && (
                  <div>
                    <Descriptions bordered column={1} size="small">
                      {get(patientProfile, "code") && (
                        <Descriptions.Item label={this.props.i18n.t("Subject ID")}>
                          {get(patientProfile, "code")}
                        </Descriptions.Item>
                      )}
                      {get(patientProfile, "randomization_date") && (
                        <Descriptions.Item label={this.props.i18n.t("Randomization date")}>
                          {moment(
                            get(patientProfile, "randomization_date")
                          ).format("YYYY MMM DD, HH:mm")}{" "}
                          UTC
                        </Descriptions.Item>
                      )}
                      {get(patientProfile, "drug.drug_status") && (
                        <Descriptions.Item label={this.props.i18n.t("Drug status")}>
                          {get(patientProfile, "drug.drug_status")}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </div>
                )}

                <br />
                <br />

                {get(patientProfile, 'consent_data') &&
                get(patientProfile, 'consent_data.consentExistsLink') &&
                get(patientProfile, 'consent_data.consentExistsLink').length > 0 ? (
                  map(
                    get(patientProfile, 'consent_data.consentExistsLink'),
                    ({ link, name }, i) => (
                      <div key={i}>
                        {link ? (
                          <FileLink url={link} text={name} />
                        ) : (
                          <div style={{ margin: "0 0 10px" }}>
                            <div style={{ margin: "0 0 5px" }}>
                              {this.props.i18n.t("Consent file missing.")}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  )
                ) : (
                  <div style={{ margin: "0 0 10px" }}>
                    {get(patientProfile, 'consent_data') &&
                    get(patientProfile, 'consent_data.consentExistsPersonType') ===
                      "Emergency" &&
                    !!get(patientProfile, 'consent_data.details.consent_upload_later') ? (
                      <div style={{ margin: "0 0 5px" }}>
                        {this.props.i18n.t(
                          "Emergency consent file upload skipped."
                        )}
                      </div>
                    ) : (
                      <span>
                        <div style={{ margin: "0 0 5px" }}>
                          {this.props.i18n.t("Consent is not provided")}
                        </div>
                      </span>
                    )}
                  </div>
                )}

                <br />
                <br />

                <h5 className="headline-default">{this.props.i18n.t("Patient event summary")}</h5>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <td>{this.props.i18n.t("Event")}</td>
                      <td>{this.props.i18n.t("Status")}</td>
                      <td>
                        {this.props.i18n.t(
                          "NamespacePatientProfileForm:Occurred at site"
                        )}
                      </td>
                      <td>
                        {this.props.i18n.t(
                          "NamespacePatientProfileForm:Data entered / Audit Trial"
                        )}
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {get(patientProfile, "events_summary_report", []).map(
                      (item, i) => (
                        <tr key={i}>
                          <td>{item.event}</td>
                          <td>
                            {item.status === "green" && (
                              <Icon
                                type="check-square"
                                className={classes.checkIcon}
                              />
                            )}
                            {item.status === "red" && (
                              <Icon
                                type="warning"
                                className={classes.warningIcon}
                              />
                            )}
                            {item.status === "orange" && (
                              <Icon
                                type="clock-circle"
                                className={classes.clockIcon}
                              />
                            )}
                            {item.status === "missing" && (
                              <Icon
                                type="clock-circle"
                                className={classes.clockIconMissing}
                              />
                            )}
                            {item.status === "gray" && (
                              <Icon
                                type="clock-circle"
                                className={classes.checkIconGray}
                              />
                            )}
                            {item.status === "pending" && (
                              <Icon
                                type="clock-circle"
                                className={classes.checkIconGray}
                              />
                            )}
                          </td>
                          <td>{item.site}</td>
                          <td>
                            <div style={{ maxWidth: "500px" }}>
                              <p>
                                <EventMessage message={item.event_comments} />
                              </p>

                              {previewEvents[get(item, "event")] && (
                                <div>
                                  {get(item, "other_details.messages", []).map(
                                    (message, index) => (
                                      <p key={index}>{message}</p>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>

                {errorMessage && (
                  <Alert
                    message={errorMessage}
                    type="error"
                    className={classes.alertsMb}
                  />
                )}
              </Col>
            </Row>
          </Content>
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  token: getUserToken(state),
  theme: getTheme(state),
});

const mapDispatchToProps = {
  login,
  loginTwoFactor,
};

export default isPatient(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withNamespaces()(PatientDashboard))
);



// WEBPACK FOOTER //
// ./src/containers/PatientDashboard/PatientDashboard.js